import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';
import moment from 'moment';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { AbilityService } from '../services/ability.service';
import { ToastService } from 'services/toast.service';

import { ABILITY_ID, ROUTES } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { Ability, Client, RootState, Site, UserOfAbilityLinked, UserFilter, Option, MultiSelect, CommonGlobalService } from '@umanforge/common';
import { ModifyAbility } from 'interfaces/api_ability.interfaces';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Select } from '../components/inputs/select.component';
import { ColorPicker } from '../components/inputs/color_picker.component';
import { ExpirationAbility } from '../components/users/expiration_ability.component';
import { Loading } from '../components/loading.component';
import { ErrorModal } from 'components/modals/error_modal.component';
import { BodyConfirmModal } from 'components/modals/body/body_confirm_modal.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { UsersIcon } from '../components/icons/users_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { BinIcon } from '../components/icons/bin_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';
import { AbilitiesIcon } from '../components/icons/abilities_icon.component';

const toastService: ToastService = new ToastService( 'Details Ability' );
const defaultChangedUsers = { added: [], removed: [] };


export function DetailsAbilityView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ ability, setAbility ] = useState<Ability | null>( null );
  const [ siteOfAbility, setSiteOfAbility ] = useState<Option<Site> | undefined>();
  const [ clientOfSite, setClientOfSite ] = useState<Option<Client> | undefined>();

  const [ modifyAbility, setModifyAbility ] = useState<ModifyAbility>( {} );
  const [ changedUsers, setChangedUsers ] = useState<{ added: UserOfAbilityLinked[], removed: UserOfAbilityLinked[] }>( defaultChangedUsers );
  const [ defaultUsers, setDefaultUsers ] = useState<Option<UserOfAbilityLinked>[]>( [] );
  const [ selectedUsers, setSelectedUsers ] = useState<Option<UserOfAbilityLinked>[]>( [] );

  const [ canPut, setCanPut ] = useState<boolean>( false );
  const [ hasChanged, setHasChanged ] = useState<boolean>( false );
  const [ isAdminOfSite, setIsAdminOfSite ] = useState<boolean>( false );
  const [ confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen ] = useState( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshAbility();
    }
  }, [ me ] );


  useEffect( () => {
    if ( ability ) {
      checkCanPutAbility();
    }
  }, [ modifyAbility, changedUsers ] );

  useEffect( () => {
    const items = {
      added: selectedUsers
        .map( item => { return item.object; } )
        .filter( selectedUser => {
          return (
            ( !defaultUsers.find( currentUser => { return ( currentUser.object.id === selectedUser.id ); } ) ) ||
            ( defaultUsers.find( currentUser => { return ( currentUser.object.id === selectedUser.id ) && selectedUser.expirationDate !== currentUser.object.expirationDate; } ) )
          );
        } ),
      removed: defaultUsers
        .map( item => { return item.object; } )
        .filter( currentUser => { return !selectedUsers.find( selectedUser => { return selectedUser.object.id === currentUser.id; } ); } )
    };
    setChangedUsers( items );
  }, [ selectedUsers ] );

  /*------------------------- Requetes API User -------------------------*/
  const refreshAbility = async () => {
    setAbility( null );
    setIsLoading( true );
    setModifyAbility( {} );
    if ( params.abilityId ) {
      const refreshedAbility = await AbilityService.getAbility( params.abilityId, token, dispatch, () => { NavigationService.toAbilities( navigate, false ); } );
      const refreshedSite = await SiteService.getSite( refreshedAbility.siteId, token, dispatch, () => { NavigationService.toAbilities( navigate, false ); } );
      const refreshedClient = await ClientService.getClient( refreshedSite.clientId, token, dispatch, () => { NavigationService.toAbilities( navigate, false ); } );
      const isAdmin: boolean = ( me != null && ( CommonGlobalService.isAdminOfSite( me, refreshedSite.id ) || CommonGlobalService.isAdminOfClient( me, refreshedClient.id ) ) );
      setIsAdminOfSite( isAdmin );
      const users = await UserService.getUsersOfAbility( params.abilityId, token, dispatch, () => { NavigationService.toAbilities( navigate, false ); } );
      const abilityUsers: Option<UserOfAbilityLinked>[] = CommonGlobalService.toOptions(
        users, isAdmin, !isAdmin,
        ( item1, item2 ) => {
          const date1 = item1.expirationDate && item1.expirationDate != '' ? moment( item1.expirationDate ) : moment();
          const date2 = item2.expirationDate && item2.expirationDate != '' ? moment( item2.expirationDate ) : moment();
          if ( date1.isSame( date2, 'date' ) ) {
            return item1.fullname.toUpperCase() < item2.fullname.toUpperCase() ? -1 : 1;
          }
          return date1.isBefore( date2, 'date' ) ? -1 : 1;
        }
      );
      setSiteOfAbility( CommonGlobalService.toOptions( [ refreshedSite ] )[ 0 ] );
      setClientOfSite( CommonGlobalService.toOptions( [ refreshedClient ] )[ 0 ] );

      setDefaultUsers( structuredClone( abilityUsers ) );
      setSelectedUsers( structuredClone( abilityUsers ) );
      setAbility( refreshedAbility );
      setIsLoading( false );
    }
  };

  const deleteAbility = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      setIsLoading( true );
      let isDeleted = false;
      if ( params.abilityId ) {
        isDeleted = await AbilityService.deleteAbility( params.abilityId, token, dispatch, refreshAbility, refreshAbility );
      }
      if ( isDeleted ) {
        toastService.showInfo( t( 'The {0} authorization has been delete.' ).replace( '{0}', ( ability?.name ?? t( 'The authorization' ) ) ) );
        NavigationService.toAbilities( navigate, false );
      }
      setIsLoading( false );
      resolve( isDeleted );
    } );
  };

  const putAbility = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const isAbilityNameChange: boolean =  modifyAbility.name != null && modifyAbility.name != '' && modifyAbility.name != ability?.name;
      const isAbilityColorChange: boolean =  modifyAbility.color != null && modifyAbility.color != '' && modifyAbility.color != ability?.color;
      if ( params.abilityId && ( isAbilityNameChange || isAbilityColorChange ) ) {
        isUpdated = await AbilityService.putAbility( params.abilityId, modifyAbility, token, dispatch, refreshAbility, refreshAbility );
      }
      resolve( isUpdated );
    } );
  };

  const addUsersToAbility = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( ability?.id && changedUsers.added.length > 0 ) {
        const body = { users: changedUsers.added.map( user => { return { id: user.id, expirationDate: user.expirationDate }; } ) };
        isUpdated = await AbilityService.postUsersToAbility( ability?.id, body, token, dispatch, refreshAbility, refreshAbility );
      }
      resolve( isUpdated );
    } );
  };

  const removedUsersToAbility = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( ability?.id && changedUsers.removed.length > 0 ) {
        const body = { users: changedUsers.removed.map( user => { return user.id; } ) };
        isUpdated = await AbilityService.deleteUsersToAbility( ability?.id, body, token, dispatch, refreshAbility, refreshAbility );
      }
      resolve( isUpdated );
    } );
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserOfAbilityLinked>[] }> => {
    if ( filter.filterName === undefined || filter.filterName === '' ) {
      delete filter.filterName;
    }
    if ( siteOfAbility != null ) {
      filter.filterSites = [ siteOfAbility.value ];
    }

    const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
    return { total: result.total, list: CommonGlobalService.toOptions<UserOfAbilityLinked>(
      result.users.map<UserOfAbilityLinked>( user => {
        return {
          id: user.id,
          fullname: user.fullname,
          isDisable: user.isDisable,
          isAdminOfUser: user.isAdminOfUser,
          expirationDate: selectedUsers.find( selectedUser => { return selectedUser.value === user.id; } )?.object.expirationDate ?? moment().add( 1, 'month' ).startOf( 'day' ).toISOString(),
        };
      } ) )
    };
  };

  /*---------------------------- Functions ------------------------------*/
  const onSaveClick = async () => {
    let isUserModify = false;
    setIsLoading( true );
    if ( ability ) {
      const isPutAbility = await putAbility();
      const isAddUsersToAbility = await addUsersToAbility();
      const isRemovedUsersToAbility = await removedUsersToAbility();
      isUserModify = isPutAbility ||
        isAddUsersToAbility ||
        isRemovedUsersToAbility;
    }
    if ( isUserModify ) {
      toastService.showInfo( t( 'The {0} authorization has been modify.' ).replace( '{0}', ( ability?.name ?? t( 'The authorization' ) ) ) );
      await refreshAbility();
    }
    setIsLoading( false );
  };

  const checkCanPutAbility = () => {
    const isAbilityNameChange: boolean =  modifyAbility.name != null && modifyAbility.name != ability?.name;
    const isAbilityColorChange: boolean =  modifyAbility.color != null && modifyAbility.color != ability?.color;
    const isUsersChange: boolean = changedUsers.added.length > 0 || changedUsers.removed.length > 0;

    setCanPut(
      isAbilityNameChange ||
      isAbilityColorChange ||
      isUsersChange
    );
    setHasChanged(
      isAbilityNameChange ||
      isAbilityColorChange ||
      isUsersChange
    );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChanged } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Modify authorization' ) } needConfirmationToNav={ hasChanged }/>
        <Breadcrumb
          needConfirmationToNav={ hasChanged }
          links={
            [
              { label: t( 'common:authorization' ), route: ROUTES.ABILITIES, icon: ( <AbilitiesIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: ability ? ability.name : t( 'Modify authorization' ), route: ROUTES.ABILITY.replace( ABILITY_ID, params.abilityId ?? '' ), icon: ( <AbilitiesIcon className='h-[14px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading && ability ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ clientOfSite }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ [ clientOfSite ] }
                  />
                </div>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ siteOfAbility }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ [ siteOfAbility ] }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_ABILITY_NAME }
                    label={ t( 'Authorization name' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'Authorization' ) + '...' }
                    icon={ <AbilitiesIcon className='w-7 h-7' /> }
                    value={ ability.name ?? ''}
                    onChange={ newAbilityName => {
                      modifyAbility.name = newAbilityName;
                      setModifyAbility( structuredClone( modifyAbility ) );
                    } }
                  />
                </div>

                <div className='w-1/4 min-w-[225px] self-start'>
                  <ColorPicker
                    label={ t( 'Colour of authorization' ) }
                    disabled={ !isAdminOfSite }
                    value={ ability.color ?? '' }
                    onBlur={ newAbilityColor => {
                      modifyAbility.color = newAbilityColor;
                      setModifyAbility( structuredClone( modifyAbility ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'User' ) + '(' + selectedUsers.length + ')' }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedUsers }
                    getValues={ getUsers }
                    onChange={ options => {
                      setSelectedUsers( options );
                    } }
                    extra={ user => {
                      return (
                        <ExpirationAbility
                          ability={ {
                            id: user.object.id,
                            siteId: ability.siteId,
                            name: ability.name,
                            color: ability.color,
                            expirationDate: user.object.expirationDate
                          } }
                          onChange={ changedAbility => {
                            user.object.expirationDate = changedAbility.expirationDate;
                            const findedUser = selectedUsers.find( item => { return changedAbility.id == item.object.id;  } );
                            if ( findedUser ) {
                              findedUser.object.expirationDate = changedAbility.expirationDate;
                            }
                            setSelectedUsers(
                              structuredClone( selectedUsers )
                                .sort( ( item1, item2 ) => {
                                  const date1 = item1.object.expirationDate && item1.object.expirationDate != '' ? moment( item1.object.expirationDate ) : moment();
                                  const date2 = item2.object.expirationDate && item2.object.expirationDate != '' ? moment( item2.object.expirationDate ) : moment();
                                  if ( date1.isSame( date2, 'date' ) ) {
                                    return item1.object.fullname.toUpperCase() < item2.object.fullname.toUpperCase() ? -1 : 1;
                                  }
                                  return date1.isBefore( date2, 'date' ) ? -1 : 1;
                                } )
                            );
                          } }
                        />
                      );
                    } }
                  />
                </div>

                <div className='flex justify-center w-full h-14 mt-10'>
                  {
                    isAdminOfSite ?
                      <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                        <Button
                          label={ t( 'Delete' ) }
                          icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                          color='#f87171'
                          colorHover='#ef4444'
                          onClick={ () => { setConfirmDeleteModalIsOpen( true ); } }
                        />
                      </div>
                      :
                      null
                  }
                  <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                    <Button
                      label={ t( 'save' ) }
                      disable={ !canPut }
                      icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                      color='#0bb173'
                      colorHover='#088D5C'
                      onClick={ onSaveClick }
                    />
                  </div>
                </div>
              </form>

              { confirmDeleteModalIsOpen ?
                <ErrorModal
                  title={ t( 'Deletion of {0} !' ).replace( '{0}', ability.name )  }
                  isOpen={ confirmDeleteModalIsOpen }
                  onClose={ () => { setConfirmDeleteModalIsOpen( false ); } }
                >
                  <BodyConfirmModal
                    onCancel={ () => { setConfirmDeleteModalIsOpen( false ); } }
                    onConfirm={ async () => {
                      const isDeleted = await deleteAbility();
                      if ( isDeleted ) {
                        NavigationService.toAbilities( navigate, false );
                      }
                      setConfirmDeleteModalIsOpen( false );
                    } }
                  >
                    <span className='flex flex-col w-full h-full text-[14px] font-[475] text-gray-600 p-3'>
                      { t( 'Are you sure you want to delete this authorization ?' ) }
                    </span>
                  </BodyConfirmModal>
                </ErrorModal>
                :
                null
              }
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}